import React, { useState } from 'react';
import VisibilitySensor from 'react-visibility-sensor';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import 'animate.css';
import './TransitionEffectsDemo.css';

const features = [
  {
    title: 'Objective Driven',
    description: 'Our software range is meticulously designed to align with your goals & objectives, ensuring every feature serves your needs.'
  },
  {
    title: 'Comprehensive',
    description: 'Our software offers a comprehensive solution, addressing every aspect of your requirements with no gaps in features.'
  },
  {
    title: 'Scalable',
    description: 'The number of features can be adjusted based on the client\'s business size and budget to effectively meet diverse customer needs.'
  },
  {
    title: 'Reliable',
    description: 'Trust is paramount, and our software consistently delivers reliable results, ensuring stability and consistency in every use case.'
  },
  {
    title: 'User Friendly',
    description: 'Designed with user-friendly interfaces and workflows, it prioritizes user-comfort, ensuring easy navigation and effective use.'
  },
  {
    title: 'Customer Support',
    description: 'With responsive support, we\'re committed to assisting you at every step to ensure a satisfying experience.'
  }
];

// Animation class mappings for animate.css
const animationClasses = {
  fadeIn: 'animate__animated animate__fadeIn',
  slideLeft: 'animate__animated animate__slideInLeft',
  slideRight: 'animate__animated animate__slideInRight',
  slideUp: 'animate__animated animate__slideInUp',
  zoomIn: 'animate__animated animate__zoomIn',
  bounceIn: 'animate__animated animate__bounceIn',
  fadeInUp: 'animate__animated animate__fadeInUp',
  fadeInLeft: 'animate__animated animate__fadeInLeft',
  fadeInRight: 'animate__animated animate__fadeInRight',
  flipInX: 'animate__animated animate__flipInX',
  lightSpeedIn: 'animate__animated animate__lightSpeedInRight'
};

function TransitionEffectsDemoAlt() {
  const [selectedEffect, setSelectedEffect] = useState('fadeInUp');
  const [isVisible, setIsVisible] = useState(false);
  const [items, setItems] = useState(features);

  const handleVisibilityChange = (visible) => {
    if (visible && !isVisible) {
      setIsVisible(true);
    }
  };

  const resetAnimation = () => {
    setIsVisible(false);
    setTimeout(() => {
      setIsVisible(true);
    }, 100);
  };

  const currentAnimationClass = animationClasses[selectedEffect] || animationClasses.fadeInUp;

  return (
    <section className="transition-demo-section ptb-100">
      <div className="container">
        <div className="section-heading text-center mb-5">
          <h2>Transition Effects Demo</h2>
          <p>Choose an animation style to see how it looks with your content</p>
          <small className="text-muted">Using Animate.css + React Transition Group</small>
        </div>

        {/* Effect Selector */}
        <div className="effect-selector mb-5">
          <div className="row">
            {Object.keys(animationClasses).map((effect) => (
              <div key={effect} className="col-6 col-md-4 col-lg-3 mb-3">
                <button
                  className={`effect-btn ${selectedEffect === effect ? 'active' : ''}`}
                  onClick={() => {
                    setSelectedEffect(effect);
                    resetAnimation();
                  }}
                >
                  {effect.replace(/([A-Z])/g, ' $1').trim()}
                </button>
              </div>
            ))}
          </div>
        </div>

        <VisibilitySensor onChange={handleVisibilityChange} partialVisibility>
          <div className="demo-content-wrapper">
            <div className="row align-items-center justify-content-between">
              {/* List Section */}
              <div className="col-md-12 col-lg-6">
                <div className="feature-contents">
                  <ul className="check-list-wrap">
                    <TransitionGroup component={null}>
                      {isVisible && items.map((feature, index) => (
                        <CSSTransition
                          key={index}
                          timeout={600 + index * 100}
                          classNames="list-item"
                          appear
                        >
                          <li
                            className={`demo-list-item ${currentAnimationClass}`}
                            style={{
                              animationDelay: `${index * 0.1}s`,
                              animationDuration: '0.6s'
                            }}
                          >
                            <div className="check-icon">
                              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                <path
                                  d="M16.6667 5L7.50004 14.1667L3.33337 10"
                                  stroke="#4E8F5A"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </div>
                            <div className="feature-text">
                              <b>{feature.title}:</b> {feature.description}
                            </div>
                          </li>
                        </CSSTransition>
                      ))}
                    </TransitionGroup>
                  </ul>
                </div>
              </div>

              {/* Image Section */}
              <div className="col-md-6 col-lg-6">
                <div 
                  className={`mask-image ${isVisible ? 'animate__animated animate__zoomIn' : ''}`}
                  style={{
                    animationDelay: '0.5s',
                    animationDuration: '0.8s'
                  }}
                >
                  <img 
                    src="/assets/img/home_new.png" 
                    className="img-fluid demo-image" 
                    alt="demo"
                  />
                </div>
              </div>
            </div>
          </div>
        </VisibilitySensor>

        {/* Info Box */}
        <div className="info-box mt-5">
          <div className="alert alert-info">
            <h5>💡 Current Effect: <strong>{selectedEffect.replace(/([A-Z])/g, ' $1').trim()}</strong></h5>
            <p className="mb-0">
              This version uses <strong>Animate.css</strong> and <strong>React Transition Group</strong> (already installed).
              Scroll up and down to reset the animation, or click any effect button above to see it in action.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

export default TransitionEffectsDemoAlt;
